import { FC, useMemo } from 'react'

import styled, { useTheme } from 'styled-components'
import { Controller, useForm, useWatch } from 'react-hook-form'

import { variables } from 'src/Styled/variables'
import dayJs from 'src/Helpers/dayjs'
import { formatDate } from 'src/Constants/formatDate'
import { PrimaryTextArea } from 'src/Components/PrimaryTextArea'
import { RateStars } from 'src/Components/RateStars'
import { CTAButton } from 'src/Components/Buttons/CTAButton'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`

const RateBlockHeaderTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`

const HeaderWrapper = styled.div`
  display: flex;
  gap: 16px;
  align-self: stretch;
  align-items: flex-end;
  justify-content: space-between;
`

const Title = styled.span`
  font-family: 'Segoe UI';
  color: ${({ theme }) => theme.color.darkGrey};
  text-align: center;
  font-size: ${variables.fontSize18};
  font-style: normal;
  font-weight: ${variables.fontWeight600};
  line-height: 150%;
`

const Text = styled.span`
  font-family: 'Segoe UI';
  color: ${({ theme }) => theme.color.darkGrey};

  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
`

const StyledCTAButton = styled(CTAButton)`
  align-self: center;
  padding-left: 48px;
  padding-right: 48px;
`
export interface RateFormData {
  rate: number
  comment: string
}

export interface RateBlockProps {
  rate?: number
  rateDeadline: string
  feedback?: string
  onSubmit: (value: RateFormData) => void
  isRateLoading?: boolean
  loading?: boolean
}

export const RateBlock: FC<RateBlockProps> = ({
  rate,
  feedback,
  rateDeadline,
  onSubmit,
  isRateLoading,
  loading,
}) => {
  const theme = useTheme()
  const { control, handleSubmit } = useForm<RateFormData>()
  const currentRate = useWatch({ control, name: 'rate' })
  const currentComment = useWatch({ control, name: 'comment' })
  const isRateDatePassed = dayJs().isAfter(rateDeadline)

  const isReadyToRate = useMemo(
    () => Boolean(currentRate) && !rate,
    [currentRate, rate],
  )

  const dateText = useMemo(() => {
    if (rate) {
      return 'Video rated'
    }

    if (!rate && isRateDatePassed) {
      return `Rating deadline was ${dayJs(rateDeadline).format(formatDate.formatDateFirst2)}`
    }

    if (!rate && !isRateDatePassed) {
      return `Rate this video till ${dayJs(rateDeadline).format(formatDate.formatDateFirst2)}`
    }

    return ''
  }, [isRateDatePassed, rate, rateDeadline])

  const currentRateStars = useMemo(
    () =>
      rate ? (
        <RateStars
          defaultValue={rate}
          value={rate}
          disabled
          style={{
            fontSize: '14px',
            color: theme.color.mediumOrange,
          }}
        />
      ) : null,
    [rate, theme.color.mediumOrange],
  )

  const rateStars = useMemo(
    () =>
      !rate ? (
        <Controller
          name='rate'
          disabled={!rate && isRateDatePassed}
          control={control}
          render={({ field }) => <RateStars {...field} />}
        />
      ) : null,
    [control, isRateDatePassed, rate],
  )

  const placeholder =
    currentRate > 2
      ? 'Share your overall thoughts on this video'
      : 'Why this video didn`t meet your expectations'

  if (loading) {
    return null
  }

  return (
    <Container>
      <HeaderWrapper>
        <RateBlockHeaderTextWrapper>
          <Title>Rate</Title>
          <Text>{dateText}</Text>
        </RateBlockHeaderTextWrapper>

        {currentRateStars}
      </HeaderWrapper>
      {rateStars}
      {feedback && <Text>{feedback}</Text>}
      {isReadyToRate && (
        <Controller
          name='comment'
          control={control}
          render={({ field }) => (
            <PrimaryTextArea
              style={{ height: 180, resize: 'none' }}
              placeholder={placeholder}
              count={{
                show: true,
                max: 500,
              }}
              {...field}
              onChange={(value) => {
                field.onChange(value)
              }}
            />
          )}
        />
      )}
      {isReadyToRate && onSubmit && (
        <StyledCTAButton
          onClick={handleSubmit(onSubmit)}
          disabled={!currentComment}
          loading={isRateLoading}
        >
          Confirm rate
        </StyledCTAButton>
      )}
    </Container>
  )
}
